/**
 * TypeScript types for Meridian v2 ElectroDB entities
 * Single source of truth for all database types
 */

// ============================================================================
// BRANDED TYPES - For type safety and clarity
// ============================================================================

/**
 * Branded type for Chat IDs
 * Ensures type safety and prevents mixing with other string IDs
 */
export type ChatId = string & { readonly __brand: 'ChatId' }
export const toChatId = (id: string): ChatId => id as ChatId

/**
 * Branded type for User ID (Supabase UUID)
 * Ensures type safety for user identification
 */
export type UserId = string & { readonly __brand: 'UserId' }
export const toUserId = (id: string): UserId => id as UserId

/**
 * @deprecated Use UserId instead - CognitoSub is kept for backwards compatibility
 */
export type CognitoSub = UserId
export const toCognitoSub = (sub: string): CognitoSub => sub as CognitoSub

/**
 * Branded type for Timestamps (ISO 8601)
 * Ensures consistency in timestamp format
 */
export type Timestamp = string & { readonly __brand: 'Timestamp' }
export const toTimestamp = (ts: string): Timestamp => ts as Timestamp

/**
 * Branded type for Message IDs
 */
export type MessageId = string & { readonly __brand: 'MessageId' }
export const toMessageId = (id: string): MessageId => id as MessageId

// ============================================================================
// FILE TYPES - For file attachments in messages
// ============================================================================

/**
 * File part in a message
 * Represents a file attachment with metadata and optional extracted text
 */
export interface FilePart {
  type: 'file'
  fileId: string           // UUID
  filename: string
  contentType: string      // e.g., 'application/pdf', 'image/png'
  size: number             // bytes
  url: string              // Supabase Storage URL (publicly fetchable — required for image vision)
  /**
   * AI SDK file-part media type. Mirrors `contentType`; present so the part is a
   * valid AI SDK `FileUIPart` ({ type:'file', mediaType, url }) when forwarded to
   * the model. Set for images so the Bedrock provider can download + send them as
   * an image content block (vision). Optional for back-compat with stored rows.
   */
  mediaType?: string
  /**
   * Document extract text for the model. After S3 offload this is a short
   * structure-aware preview; full text lives at `extractedS3Key`.
   * Images use vision instead — no extractedText.
   */
  extractedText?: string
  /** S3 object key for the full extract (`private/meridian/chat/.../extract.txt`). */
  extractedS3Key?: string
  /** Char length of the full extract stored in S3 (before preview). */
  extractedFullLength?: number
  /** True when `extractedText` is a preview and full text is at `extractedS3Key`. */
  extractedIsPreview?: boolean
  uploadedAt: string       // ISO 8601 timestamp
}

/**
 * File upload response from the upload endpoint
 */
export interface FileUploadResponse {
  success: boolean
  data?: FilePart
  error?: string
}

// ============================================================================
// ENUMS & CONSTANTS
// ============================================================================

export const CHAT_STATUS = {
  ACTIVE: 'active',
  ARCHIVED: 'archived',
  DELETED: 'deleted',
} as const

export type ChatStatus = typeof CHAT_STATUS[keyof typeof CHAT_STATUS]

export const MESSAGE_ROLE = {
  USER: 'user',
  ASSISTANT: 'assistant',
  SYSTEM: 'system',
} as const

export type MessageRole = typeof MESSAGE_ROLE[keyof typeof MESSAGE_ROLE]

// ============================================================================
// ENTITY TYPES
// ============================================================================

/**
 * Message in a chat conversation
 * Stored with UIMessage parts (text, tool-call, tool-result, file, etc.)
 * 
 * Part types and their properties:
 * - text: { type: 'text', text: string }
 * - tool-call: { type: 'tool-call', toolCallId: string, toolName: string, args: object, providerExecuted?: boolean }
 * - tool-result: { type: 'tool-result', toolCallId: string, toolName: string, output: object }
 * - tool-approval-request: { type: 'tool-approval-request', toolCallId: string, toolName: string, input: object }
 * - tool-approval-response: { type: 'tool-approval-response', toolCallId: string, approved: boolean }
 * - file: { type: 'file', fileId: string, filename: string, contentType: string, size: number, url: string, extractedText?: string, uploadedAt: string }
 * - Other: step-start, reasoning, data-*, etc. (preserved as-is)
 * 
 * NOTE: state and approval properties are NOT persisted to the database.
 * These are UI-only concerns managed by the frontend:
 * - state (pending/executing/done) is transient UI state
 * - approval (approved/rejected) is used during the approval flow
 * The database stores only the canonical tool-call/tool-result structure.
 * 
 * File parts are persisted as-is and used for:
 * - Displaying file attachments in the UI
 * - Providing file context to the AI model
 * - Enabling file downloads
 */
export interface Message {
  id: MessageId
  role: MessageRole
  parts: Array<Record<string, unknown>>  // UIMessage parts from AI SDK (text, tool-call, tool-result, etc.)
  timestamp: Timestamp
  metadata?: Record<string, unknown>
}

/**
 * Chat Summary - Lightweight entity for listing chats
 * Used for efficient pagination and chat listing without full message history
 */
export interface ChatSummary {
  id: ChatId
  userId: CognitoSub
  entityType: 'chat-summary'
  title: string
  messageCount: number
  status: ChatStatus
  sharedWith?: Array<{
    userId: CognitoSub
    email: string
    sharedAt: Timestamp
  }>
  originalOwnerId?: CognitoSub
  createdAt: Timestamp
  updatedAt: Timestamp
  lastMessageAt: Timestamp
}

/**
 * Full Chat - Complete conversation with all messages
 * Used for retrieving full chat history
 */
export interface Chat {
  id: ChatId
  userId: CognitoSub
  entityType: 'chat'
  title: string
  messages: Message[]
  status: ChatStatus
  sharedWith?: Array<{
    userId: CognitoSub
    email: string
    sharedAt: Timestamp
  }>
  originalOwnerId?: CognitoSub
  createdAt: Timestamp
  updatedAt: Timestamp
  lastMessageAt: Timestamp
}

/**
 * Conversation Summary - Tier 2 Memory
 * Stores periodic summaries of conversations to preserve:
 * - User's goals and intent
 * - Key decisions made
 * - Important preferences
 * - Constraints and requirements
 * - Progress and status
 */
export interface ConversationSummary {
  id: ChatId
  userId: CognitoSub
  entityType: 'conversation-summary'
  summary: string
  keyDecisions?: string[]
  topics?: string[]
  messageCount: number
  lastSummarizedAt: Timestamp
  createdAt: Timestamp
  updatedAt: Timestamp
}

/**
 * Semantic Memory - Tier 3 Memory
 * Stores user-specific facts, preferences, decisions, and domain knowledge
 * Stored in Zilliz for semantic search
 */
export interface SemanticMemory {
  memoryId: string
  userId: CognitoSub
  memoryType: 'preference' | 'decision' | 'constraint' | 'fact'
  content: string
  embedding: number[] // 1536-dimensional vector
  createdAt: Timestamp
  updatedAt: Timestamp
}

// ============================================================================
// RELATION TYPES - For API responses
// ============================================================================

/**
 * Chat with related data
 */
export interface ChatWithRelations extends Chat {
  summary?: ConversationSummary
  semanticMemories?: SemanticMemory[]
}

/**
 * Chat Summary with related data
 */
export interface ChatSummaryWithRelations extends ChatSummary {
  summary?: ConversationSummary
}

// ============================================================================
// REQUEST/RESPONSE TYPES
// ============================================================================

/**
 * Create Chat Request
 */
export interface CreateChatRequest {
  title?: string
}

/**
 * Create Chat Response
 */
export interface CreateChatResponse {
  success: boolean
  data?: {
    chatId: ChatId
    title: string
    createdAt: Timestamp
  }
  error?: string
}

/**
 * List Chats Response
 */
export interface ListChatsResponse {
  success: boolean
  data?: {
    chats: ChatSummaryWithRelations[]
    cursor?: string
    hasMore: boolean
  }
  error?: string
}

/**
 * Get Chat Response (with optional pagination cursor/hasMore)
 */
export interface GetChatResponse {
  success: boolean
  data?: ChatWithRelations
  cursor?: string | null
  hasMore?: boolean
  error?: string
}

/**
 * Send Message Request (API accepts chatId + content for streaming)
 */
export interface SendMessageRequest {
  chatId?: string
  message?: string
  content?: string
  role?: MessageRole
}

/**
 * Send Message Response (Streaming)
 */
export interface SendMessageResponse {
  success: boolean
  data?: {
    messageId: MessageId
    role: MessageRole
    content: string
    timestamp: Timestamp
  }
  error?: string
}

/**
 * Update Chat Request
 */
export interface UpdateChatRequest {
  title?: string
  status?: ChatStatus
}

/**
 * Update Chat Response
 */
export interface UpdateChatResponse {
  success: boolean
  data?: {
    chatId: ChatId
    title: string
    updatedAt: Timestamp
  }
  error?: string
}

/**
 * Delete Chat Response
 */
export interface DeleteChatResponse {
  success: boolean
  data?: {
    message: string
  }
  error?: string
}

/**
 * Share Chat Request
 */
export interface ShareChatRequest {
  email: string
}

/**
 * Share Chat Response
 */
export interface ShareChatResponse {
  success: boolean
  data?: {
    chatId: ChatId
    sharedWith: string[]
  }
  error?: string
}

/**
 * Archive Chat Response
 */
export interface ArchiveChatResponse {
  success: boolean
  data?: {
    chatId: ChatId
    status: ChatStatus
  }
  error?: string
}

/**
 * Unarchive Chat Response
 */
export interface UnarchiveChatResponse {
  success: boolean
  data?: {
    chatId: ChatId
    status: ChatStatus
  }
  error?: string
}

